import { Form, Input, Modal, Select } from 'antd';
import { useCallback, useEffect, useState } from 'react';

import {
  fetchAllQuestionModulesAsAdmin,
  fetchAnswerOptionSetsAsAdmin,
  fetchQuestionsAsAdmin,
} from '@/apis/admin';
import { fetchOptions } from '@/apis/options';
import { AnswerType, Option } from '@/enums';
import { Options } from '@/types';
import {
  AdminAnswerOptionSet,
  AdminQuestion,
  AdminQuestionModule,
} from '@/types/apis/admin';
import { getAnswerOptionSetLabel, getModuleLabel } from '@/utils/adminLabels';

const { TextArea } = Input;
const { Option: SelectOption } = Select;

type Question = AdminQuestion;

type Props = {
  isOpen: boolean;
  question?: Question | null;
  onClose: () => void;
  onSave: (
    question: Question | Omit<AdminQuestion, 'id' | 'createdAt' | 'updatedAt'>,
  ) => void;
  mode: 'add' | 'edit';
};

export default function QuestionModal({
  isOpen,
  question,
  onClose,
  onSave,
  mode,
}: Props) {
  const [form] = Form.useForm();
  const [industries, setIndustries] = useState<Options>([]);
  const [modules, setModules] = useState<AdminQuestionModule[]>([]);
  const [answerOptionSets, setAnswerOptionSets] = useState<
    AdminAnswerOptionSet[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [parentQuestions, setParentQuestions] = useState<AdminQuestion[]>([]);
  const [loadingParentQuestions, setLoadingParentQuestions] = useState(false);

  // 檢查是否會形成循環的遞迴函數
  const wouldCreateCycle = (
    potentialParentId: string,
    currentQuestionId: string,
  ): boolean => {
    if (!potentialParentId || !currentQuestionId) return false;
    if (potentialParentId === currentQuestionId) return true;

    // 尋找潛在父問題的所有祖先
    const findAncestors = (
      questionId: string,
      visited = new Set<string>(),
    ): string[] => {
      if (visited.has(questionId)) return []; // 已經遇到循環，停止查找
      visited.add(questionId);

      const question = parentQuestions.find((q) => q.id === questionId);
      if (!question?.parentQuestionId) return [];

      const ancestors = findAncestors(question.parentQuestionId, visited);
      return [question.parentQuestionId, ...ancestors];
    };

    const ancestors = findAncestors(potentialParentId);
    return ancestors.includes(currentQuestionId);
  };

  const loadIndustries = async () => {
    try {
      const response = await fetchOptions(Option.Industry);
      setIndustries(response.data);
    } catch (err) {
      console.error('Error loading industries:', err);
    }
  };

  const loadModules = async () => {
    try {
      const response = await fetchAllQuestionModulesAsAdmin();
      setModules(response.data.items);
    } catch (err) {
      console.error('Error loading modules:', err);
    }
  };

  const loadAnswerOptionSets = async () => {
    try {
      const response = await fetchAnswerOptionSetsAsAdmin(1, 100);
      setAnswerOptionSets(response.data.items);
    } catch (err) {
      console.error('Error loading answer option sets:', err);
    }
  };

  const loadParentQuestions = useCallback(
    async (moduleId?: string, industryId?: string) => {
      if (!moduleId) {
        setParentQuestions([]);
        return;
      }

      // For SDGs module, require industry selection
      const selectedModule = modules.find((m) => m.id === moduleId);
      const isSDGsModule = selectedModule?.name.toLowerCase() === 'sdgs';
      if (isSDGsModule && !industryId) {
        setParentQuestions([]);
        return;
      }

      try {
        setLoadingParentQuestions(true);
        const industryName =
          isSDGsModule && industryId
            ? industries.find((ind) => ind.value === industryId)?.label
            : undefined;

        const response = await fetchQuestionsAsAdmin(
          1,
          1000,
          moduleId,
          industryName,
        );
        setParentQuestions(response.data.items);
      } catch (err) {
        console.error('Error loading parent questions:', err);
        setParentQuestions([]);
      } finally {
        setLoadingParentQuestions(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (isOpen) {
      loadIndustries();
      loadModules();
      loadAnswerOptionSets();

      if (mode === 'edit' && question) {
        form.setFieldsValue({
          question: question.question,
          answerType: question.answerType,
          moduleId: question.module?.id,
          industryId: question.industry?.id,
          information: question.information,
          parentQuestionId: question.parentQuestionId,
          answerOptionSetId: question.answerOptionSet?.id,
        });
      } else if (mode === 'add') {
        form.resetFields();
        setParentQuestions([]);
        form.setFieldsValue({
          answerType: AnswerType.Radio,
        });
      }
    }
  }, [question, mode, isOpen, form]);

  // 單獨的 useEffect 來處理編輯模式下的父問題載入
  useEffect(() => {
    if (
      isOpen &&
      mode === 'edit' &&
      question &&
      modules.length > 0 &&
      industries.length > 0
    ) {
      loadParentQuestions(question.module?.id, question.industry?.id);
    }
  }, [
    isOpen,
    mode,
    question,
    modules.length,
    industries.length,
    loadParentQuestions,
  ]);

  const handleModuleChange = useCallback(
    (moduleId: string) => {
      form.setFieldValue('moduleId', moduleId);
      form.setFieldValue('parentQuestionId', undefined); // Reset parent question

      // Check if it's SDGs module
      const selectedModule = modules.find((m) => m.id === moduleId);
      const isSDGsModule = selectedModule?.name.toLowerCase() === 'sdgs';

      if (!isSDGsModule) {
        form.setFieldValue('industryId', undefined); // Reset industry for non-SDGs modules
        loadParentQuestions(moduleId);
      } else {
        // For SDGs module, wait for industry selection
        setParentQuestions([]);
      }
    },
    [form, modules, loadParentQuestions],
  );

  const handleIndustryChange = useCallback(
    (industryId: string) => {
      form.setFieldValue('industryId', industryId);
      form.setFieldValue('parentQuestionId', undefined); // Reset parent question

      const moduleId = form.getFieldValue('moduleId');
      if (moduleId) {
        loadParentQuestions(moduleId, industryId);
      }
    },
    [form, loadParentQuestions],
  );

  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      const selectedModule = modules.find((mod) => mod.id === values.moduleId);
      const selectedIndustry = industries.find(
        (ind) => ind.value === values.industryId,
      );
      const selectedParentQuestion = values.parentQuestionId
        ? parentQuestions.find((q) => q.id === values.parentQuestionId)
        : undefined;

      const selectedAnswerOptionSet = values.answerOptionSetId
        ? answerOptionSets.find((set) => set.id === values.answerOptionSetId)
        : undefined;

      const formattedData = {
        question: values.question,
        answerType: values.answerType,
        module: selectedModule!,
        information: values.information || '',
        industry: selectedIndustry
          ? { id: selectedIndustry.value, name: selectedIndustry.label }
          : undefined,
        parentQuestionId: values.parentQuestionId || undefined,
        answerOptionSet: selectedAnswerOptionSet
          ? {
              id: selectedAnswerOptionSet.id,
              name: selectedAnswerOptionSet.name,
            }
          : undefined,
        level: selectedParentQuestion ? selectedParentQuestion.level + 1 : 1,
        order: question?.order || 0,
        isRequired: question?.isRequired ?? true,
      };

      if (mode === 'edit' && question) {
        onSave({
          ...question,
          ...formattedData,
        });
      } else {
        onSave(formattedData);
      }
      form.resetFields();
      onClose();
    } catch (error) {
      console.error('Form validation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={mode === 'edit' ? '編輯題目' : '新增題目'}
      open={isOpen}
      onOk={handleSave}
      onCancel={handleCancel}
      okText="儲存"
      cancelText="取消"
      width={600}
      confirmLoading={loading}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          label="題目內容"
          name="question"
          rules={[{ required: true, message: '請輸入題目內容' }]}
        >
          <TextArea rows={3} placeholder="請輸入題目內容..." />
        </Form.Item>

        <div style={{ display: 'flex', gap: '16px' }}>
          <Form.Item
            label="題目模組"
            name="moduleId"
            rules={[{ required: true, message: '請選擇題目模組' }]}
            style={{ flex: 1 }}
          >
            <Select placeholder="請選擇模組" onChange={handleModuleChange}>
              {modules.map((module) => (
                <SelectOption key={module.id} value={module.id}>
                  {getModuleLabel(module.name)}
                </SelectOption>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.moduleId !== currentValues.moduleId
            }
          >
            {({ getFieldValue }) => {
              const moduleId = getFieldValue('moduleId');
              const selectedModule = modules.find((m) => m.id === moduleId);
              const isSDGsModule =
                selectedModule?.name.toLowerCase() === 'sdgs';

              return isSDGsModule ? (
                <Form.Item
                  label="所屬行業"
                  name="industryId"
                  rules={[{ required: true, message: '請選擇行業' }]}
                  style={{ flex: 1 }}
                >
                  <Select
                    placeholder="請選擇行業"
                    onChange={handleIndustryChange}
                  >
                    {industries.map((industry) => (
                      <SelectOption key={industry.value} value={industry.value}>
                        {industry.label}
                      </SelectOption>
                    ))}
                  </Select>
                </Form.Item>
              ) : (
                <Form.Item
                  label="所屬行業"
                  name="industryId"
                  style={{ flex: 1 }}
                >
                  <Select
                    placeholder="通用題目"
                    allowClear
                    onChange={handleIndustryChange}
                  >
                    {industries.map((industry) => (
                      <SelectOption key={industry.value} value={industry.value}>
                        {industry.label}
                      </SelectOption>
                    ))}
                  </Select>
                </Form.Item>
              );
            }}
          </Form.Item>
        </div>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.moduleId !== currentValues.moduleId ||
            prevValues.industryId !== currentValues.industryId
          }
        >
          {({ getFieldValue }) => {
            const moduleId = getFieldValue('moduleId');
            const industryId = getFieldValue('industryId');
            const selectedModule = modules.find((m) => m.id === moduleId);
            const isSDGsModule = selectedModule?.name.toLowerCase() === 'sdgs';

            // Check if parent question selection should be disabled
            const shouldDisableParentSelection =
              !moduleId || (isSDGsModule && !industryId);

            return (
              <Form.Item
                label="父問題"
                name="parentQuestionId"
                help={
                  mode === 'edit'
                    ? '編輯模式下不能改變父子關係'
                    : shouldDisableParentSelection
                      ? isSDGsModule
                        ? '請先選擇題目模組和行業'
                        : '請先選擇題目模組'
                      : '選擇父問題，留空則為根問題'
                }
              >
                <Select
                  placeholder={
                    shouldDisableParentSelection
                      ? '請先選擇模組' + (isSDGsModule ? '和行業' : '')
                      : '選擇父問題（可選）'
                  }
                  allowClear
                  disabled={mode === 'edit' || shouldDisableParentSelection}
                  loading={loadingParentQuestions}
                >
                  {parentQuestions
                    .filter((q) => {
                      // 篩選邏輯:
                      // 1. 不能選擇自己為父問題
                      // 2. 不能選擇會形成循環的問題
                      if (mode === 'edit' && question && q.id === question.id)
                        return false;

                      // 檢查是否會形成循環
                      if (
                        mode === 'edit' &&
                        question &&
                        wouldCreateCycle(q.id, question.id)
                      )
                        return false;

                      return true;
                    })
                    .map((q) => (
                      <SelectOption key={q.id} value={q.id}>
                        L{q.level} - {q.question}
                      </SelectOption>
                    ))}
                </Select>
              </Form.Item>
            );
          }}
        </Form.Item>

        <div style={{ display: 'flex', gap: '16px' }}>
          <Form.Item
            label="回答類型"
            name="answerType"
            rules={[{ required: true, message: '請選擇回答類型' }]}
            style={{ flex: 1 }}
          >
            <Select
              options={[
                { value: AnswerType.Radio, label: '單選題' },
                { value: AnswerType.Select, label: '下拉選單' },
                { value: AnswerType.Textarea, label: '問答題' },
                { value: AnswerType.Info, label: '純資訊' },
                { value: AnswerType.Upload, label: '檔案上傳' },
              ]}
            />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.answerType !== currentValues.answerType
            }
          >
            {({ getFieldValue }) => {
              const answerType = getFieldValue('answerType');
              const isDisabled =
                answerType !== AnswerType.Radio &&
                answerType !== AnswerType.Select;

              return (
                <Form.Item
                  label="選項集"
                  name="answerOptionSetId"
                  style={{ flex: 1 }}
                >
                  <Select allowClear disabled={isDisabled}>
                    {answerOptionSets.map((set) => (
                      <SelectOption key={set.id} value={set.id}>
                        {getAnswerOptionSetLabel(set.name)}
                      </SelectOption>
                    ))}
                  </Select>
                </Form.Item>
              );
            }}
          </Form.Item>
        </div>

        <Form.Item label="補充資訊" name="information">
          <TextArea rows={2} placeholder="題目的補充說明或解釋..." />
        </Form.Item>
      </Form>
    </Modal>
  );
}
